import styled from 'styled-components';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from './Button/Button';
import Typography from './Typography';
import { theme } from '../theme';
import { useTranslation } from 'react-i18next';
import useWindowDimensions from '../hooks/useWindowDimensions';
import HelmetIcon from './Icons/HelmetIcon';
import EllipsisVerticalIcon from './Icons/EllipsisVerticalIcon';
import { useAuth } from '../context/AuthContext';
import axiosClient from '../api/axiosClient';

const ProfileHeader = ({ profileUser, user }) => {
  const { t: tUserProfle } = useTranslation('userProfile');
  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Hook para dimensiones de pantalla
  const { width } = useWindowDimensions();
  const isTablet = width < parseInt(theme.breakpoints.lg);
  const { isFollowing: ctxIsFollowing, followingReady, follow, unfollow } = useAuth();
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(null);
  const [followingCount, setFollowingCount] = useState(null);

  // Manejar click fuera del dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isFollowing = profileUser?._id ? ctxIsFollowing(profileUser._id) : false;

  const handleFollow = async () => {
    if (!profileUser?._id) return;
    setFollowLoading(true);
    try {
      await follow(profileUser._id);
      setFollowersCount(prev => prev != null ? prev + 1 : prev)
    } catch (e) {
      // noop
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!profileUser?._id) return;
    setFollowLoading(true);
    try {
      await unfollow(profileUser._id);
      setFollowersCount(prev => prev != null ? Math.max(0, prev - 1) : prev)
    } catch (e) {
      // noop
    } finally {
      setFollowLoading(false);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleEditProfile = () => {
    navigate(`/edit-profile`, { state: { returnTo: `/user/${userId}` } });
    setIsDropdownOpen(false);
  };

  const handleEditInterests = () => {
    navigate(`/edit-interests`, { state: { returnTo: `/user/${userId}` } });
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const uid = profileUser?._id || userId
        if (!uid) return
        const [follRes, ingRes] = await Promise.all([
          axiosClient.get(`/api/users/${uid}/followers/count`),
          axiosClient.get(`/api/users/${uid}/following/count`),
        ])
        setFollowersCount(Number(follRes.data?.count || 0))
        setFollowingCount(Number(ingRes.data?.count || 0))
      } catch (e) {
        setFollowersCount(0)
        setFollowingCount(0)
      }
    }
    loadCounts()
  }, [profileUser?._id, userId])

  return (
    <>
      <ProfileHeaderWrapper>
        <Container>
          <UserHeader>
            <UserDataWrapper>
              {profileUser.userAvatar ? (
                <UserAvatar src={profileUser.userAvatar} alt='user avatar' />
              ) : (
                <AvatarPlaceholder>
                  <HelmetIcon width={60} height={60} fill={theme.colors.defaultStrong} />
                </AvatarPlaceholder>
              )}
              {/* <UserAvatar src={profileUser.userAvatar} alt='user avatar' /> */}
              <UserData>
                <Typography as="h1" $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold">
                  {profileUser.name} {profileUser.lastName}</Typography>
                <CountersRow>
                  <CounterLink onClick={() => navigate(`/user/${userId}/connections?tab=followers&from=profile`, { state: { from: 'profile', returnTo: `/user/${userId}` } })}>
                    <Typography $variant="body-2-semibold" color={theme.colors.defaultWeak}>
                      {typeof followersCount === 'number' ? followersCount : ''} {tUserProfle('profileHeader.counters.followers')}
                    </Typography>
                  </CounterLink>
                  <CounterLink onClick={() => navigate(`/user/${userId}/connections?tab=following&from=profile`, { state: { from: 'profile', returnTo: `/user/${userId}` } })}>
                    <Typography $variant="body-2-semibold" color={theme.colors.defaultWeak}>
                      {typeof followingCount === 'number' ? followingCount : ''} {tUserProfle('profileHeader.counters.following')}
                    </Typography>
                  </CounterLink>
                </CountersRow>
                {/* <Typography as="p" $variant="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>{profileUser.locality}, {profileUser.country}</Typography> */}
              </UserData>
            </UserDataWrapper>
            {user && user.id !== userId && (
              <FollowActions>
                {followingReady && (
                  isFollowing ? (
                    <Button size="l" sizeTablet="l" sizeMobile="m" onClick={handleUnfollow} disabled={followLoading} $variant="outline">
                      {tUserProfle('profileHeader.action.unfollow')}
                    </Button>
                  ) : (
                    <Button size="l" sizeTablet="l" sizeMobile="m" onClick={handleFollow} disabled={followLoading} $variant="brand">
                      {tUserProfle('profileHeader.action.follow')}
                    </Button>
                  )
                )}
              </FollowActions>
            )}
            {user && user.id === userId && (
              <ProfileDropdownWrapper ref={dropdownRef}>
                <Button
                  onClick={toggleDropdown}
                  size="small"
                  $iconOnly
                  $variant="outline"
                >
                  <EllipsisVerticalIcon fill={theme.colors.defaultWeak} />
                </Button>

                <DropdownMenu $visible={isDropdownOpen}>
                  <MenuItem onClick={handleEditProfile}>
                    <Typography $variant="body-2-medium" color={theme.colors.defaultStrong}>
                      {tUserProfle('editProfile.title')}
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleEditInterests}>
                    <Typography $variant="body-2-medium" color={theme.colors.defaultStrong}>
                      {tUserProfle('editInterests.title')}
                    </Typography>
                  </MenuItem>
                </DropdownMenu>
              </ProfileDropdownWrapper>
            )}
          </UserHeader>
          <SectionTabs>
            <SectionTab
              to={`/user/${userId}`}
              className={`SectionTab ${location.pathname === `/user/${userId}` ? 'Active' : ''}`}
            >
              <Typography
                $variant="body-1-semibold" $variantMobile="body-2-semibold"
                color={location.pathname === `/user/${userId}` ? theme.colors.brandMain : theme.colors.defaultWeak}
              >
                {tUserProfle('profileHeader.tabs.events')}
              </Typography>
            </SectionTab>
            {isTablet && (
              <SectionTab
                to={`/user/${userId}/profile`}
                className={`SectionTab ${location.pathname === `/user/${userId}/profile` ? 'Active' : ''}`}
              >
                <Typography
                  $variant="body-1-semibold" $variantMobile="body-2-semibold"
                  color={location.pathname === `/user/${userId}/profile` ? theme.colors.brandMain : theme.colors.defaultWeak}
                >
                  {tUserProfle('profileHeader.tabs.profile')}
                </Typography>
              </SectionTab>
            )}
            <SectionTab
              to={`/user/${userId}/garage`}
              className={`SectionTab ${location.pathname === `/user/${userId}/garage` ? 'Active' : ''}`}
            >
              <Typography
                $variant="body-1-semibold" $variantMobile="body-2-semibold"
                color={location.pathname === `/user/${userId}/garage` ? theme.colors.brandMain : theme.colors.defaultWeak}
              >
                {tUserProfle('profileHeader.tabs.garage')}
              </Typography>
            </SectionTab>
          </SectionTabs>
        </Container>
      </ProfileHeaderWrapper>
    </>
  );
};

export default ProfileHeader;

const ProfileHeaderWrapper = styled.section`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  flex-direction: column; 
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  padding-bottom: 0;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.sizing.xs};
    padding: ${({ theme }) => theme.sizing.sm};
    padding-bottom: 0;
  }
`;

const UserHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const FollowActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const UserDataWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.sizing.sm};
  }
`;

const UserData = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const CountersRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const CounterLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 300ms ease-in;

  &:hover {
    opacity: 0.6;
  }
`;

const UserAvatar = styled.img`
  border-radius: ${({ theme }) => theme.radius.xs};
  height: 120px;
  width: 120px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
  height: 80px;
  width: 80px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
  height: 56px;
  width: 56px;
  }
`;

const AvatarPlaceholder = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  height: 120px;
  justify-content: center;
  width: 120px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 80px;
    width: 80px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 56px;
    width: 56px;
  }
`;

const SectionTabs = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const SectionTab = styled(Link)`
  align-items: flex-end;
  background-color: transparent;
  border-bottom: 4px solid transparent;
  display: flex;
  justify-content: center;
  padding: 16px 0;

  &.Active {
    border-bottom: 4px solid ${({ theme }) => theme.colors.brandMain};
    color: ${({ theme }) => theme.colors.brandMain};
  }
`;

const ProfileDropdownWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  background-color: white;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0 4px 12px rgb(0 0 0 / 12%);
  min-width: 180px;
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  padding: ${({ theme }) => theme.sizing.xs};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  position: absolute;
  right: 0;
  top: 48px;
  transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(-8px)')};
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  z-index: 100;
`;

const MenuItem = styled.div`
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;
